import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { LeadFollowup, FOLLOWUP_TYPES, dateTime, useLeadUsers } from './leads.api';
import { LeadContactButtons } from './LeadContactButtons';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Manage Follow-ups — the call list.
 *
 * Opens on **Overdue** rather than on everything: the first question of the day is what was
 * promised and missed, and a screen that opens on 4,000 rows sorted by date answers nobody's.
 * Completing a follow-up here also stamps the lead's "last contacted", so the two never disagree.
 */
const BUCKETS = [
  { key: 'overdue', label: 'Overdue', tone: 'text-rose-600' },
  { key: 'today', label: 'Today', tone: 'text-amber-600' },
  { key: 'upcoming', label: 'Upcoming', tone: 'text-slate-600' },
  { key: 'done', label: 'Done', tone: 'text-emerald-600' },
  { key: '', label: 'All', tone: 'text-slate-600' },
];

export function LeadFollowupsPage() {
  const qc = useQueryClient();
  const { data: users } = useLeadUsers();
  const [bucket, setBucket] = useState('overdue');
  const [assignedToId, setAssignedToId] = useState<string | number | ''>('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [rescheduling, setRescheduling] = useState<LeadFollowup | null>(null);
  const [newDate, setNewDate] = useState('');

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (bucket) p.bucket = bucket;
    if (assignedToId) p.assignedToId = assignedToId;
    if (type) p.type = type;
    if (q) p.q = q;
    return p;
  }, [bucket, assignedToId, type, q, page, pageSize]);

  const { data, isLoading } = useQuery({
    queryKey: ['lead-followups', params],
    queryFn: async () => (await api.get<{
      total: number; items: LeadFollowup[]; buckets: { today: number; overdue: number; upcoming: number; done: number };
    }>('/leads/followups', { params })).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lead-followups'] });
    qc.invalidateQueries({ queryKey: ['leads'] });
    qc.invalidateQueries({ queryKey: ['lead-dashboard'] });
  };

  const save = useMutation({
    mutationFn: async (v: { id: number; body: any }) => (await api.patch(`/leads/followups/${v.id}`, v.body)).data,
    onSuccess: () => { toast.success('Follow-up updated'); setRescheduling(null); setNewDate(''); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update'),
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/leads/followups/${id}`)).data,
    onSuccess: () => { toast.success('Follow-up removed'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not remove'),
  });

  const items = data?.items ?? [];

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Manage Followups</h1>
        <p className="text-xs text-gray-500 mt-0.5">Everything promised to a lead, in the order it is owed.</p>
      </div>

      <LeadsSubNav />

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          {BUCKETS.map((b) => {
            const n = b.key ? (data?.buckets as any)?.[b.key] : undefined;
            return (
              <button key={b.key || 'all'} onClick={() => { setBucket(b.key); setPage(1); }}
                className={`px-3 py-1.5 text-sm rounded-md ${bucket === b.key ? 'bg-white dark:bg-slate-900 shadow-sm font-medium ' + b.tone : 'text-slate-600 dark:text-slate-300'}`}>
                {b.label}{n !== undefined ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
        <div>
          <label className="text-xs text-gray-500 block">Assigned to</label>
          <SearchableSelect value={assignedToId} onChange={(v) => { setAssignedToId(v); setPage(1); }} allowClear placeholder="Anyone"
            options={(users ?? []).map((u) => ({ value: u.id, label: u.name }))} className="min-w-[170px]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 block">Type</label>
          <select className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {FOLLOWUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-gray-500 block">Search</label>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
            <input className="w-full border rounded pl-7 pr-2 py-1.5 text-sm dark:bg-slate-900" placeholder="Company, contact, mobile, lead no"
              value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-emerald-50 dark:bg-slate-800 text-xs uppercase text-emerald-900 dark:text-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Lead</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Reach</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Assigned To</th>
                <th className="text-left px-3 py-2">Notes</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">
                  {bucket === 'overdue' ? 'Nothing overdue — the list is clean.' : 'No follow-ups here.'}
                </td></tr>
              ) : items.map((f) => (
                <tr key={f.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className={`px-3 py-2 text-xs whitespace-nowrap ${f.overdue ? 'text-rose-600 font-medium' : ''}`}>{dateTime(f.dueAt)}</td>
                  <td className="px-3 py-2 text-xs">{f.type}</td>
                  <td className="px-3 py-2 cursor-pointer" onClick={() => setDetailId(f.leadId)}>
                    <div className="font-medium text-slate-800 dark:text-slate-100">{f.lead?.companyName}</div>
                    <div className="text-[11px] text-gray-400">{f.lead?.leadNo}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{f.lead?.contactName || '—'}</div>
                    <div className="text-gray-400">{f.lead?.mobile || ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <LeadContactButtons leadId={f.leadId} mobile={f.lead?.mobile} company={f.lead?.companyName} contactName={f.lead?.contactName} onDone={refresh} />
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      f.status === 'DONE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : f.overdue ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {f.status === 'PENDING' && f.overdue ? 'OVERDUE' : f.status}
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-xs ${f.assignedToName ? '' : 'text-gray-400 italic'}`}>{f.assignedToName || 'Unassigned'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 max-w-[220px] truncate" title={f.notes ?? ''}>{f.notes || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {f.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => {
                              const outcome = window.prompt('How did it go? (optional)', '');
                              if (outcome === null) return;
                              save.mutate({ id: f.id, body: { status: 'DONE', outcome } });
                            }}
                            className="p-1 border rounded text-emerald-700 border-emerald-200 hover:bg-emerald-50" title="Mark done">
                            <Check size={12} />
                          </button>
                          <button onClick={() => { setRescheduling(f); setNewDate(f.dueAt.slice(0, 16)); }}
                            className="p-1 border rounded text-slate-600 hover:bg-slate-50" title="Reschedule">
                            <Clock size={12} />
                          </button>
                        </>
                      )}
                      <button onClick={() => { if (window.confirm('Remove this follow-up?')) del.mutate(f.id); }}
                        className="p-1 border rounded text-rose-600 border-rose-200 hover:bg-rose-50" title="Remove">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Rows:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-1 py-0.5 dark:bg-slate-900">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
        </div>
      </div>

      {rescheduling && (
        <div className="fixed inset-0 z-[900] bg-black/40 flex items-center justify-center p-4" onClick={() => setRescheduling(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-1">Reschedule follow-up</h3>
            <p className="text-xs text-gray-500 mb-3">{rescheduling.lead?.companyName}</p>
            <input type="datetime-local" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRescheduling(null)} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
              <button disabled={!newDate} onClick={() => save.mutate({ id: rescheduling.id, body: { dueAt: newDate } })}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}

      {detailId && <LeadDetailDrawer leadId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
